import { PageHeaderMenu } from '@/components/common/PageHeader';
import WidgetHeader from '@/components/common/WidgetHeader';
import { WizardEligibility } from '@/components/cashflow/WizardEligibility';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useHideTransfersChoice } from '@/hooks/useHideTransfersChoice';
import { usePageDates } from '@/hooks/useControlledDates';
import { usePageDefaults } from '@/hooks/usePageDefaults';
import BodyHead from '@/layout/header';
import { StandardWidgetProps } from '@/models/WidgetModels';
import CashFlowAverageSpending from '@/modules/cashFlow/cashFlowAverageSpending';
import CashFlowAverageSpendingSkeleton from '@/modules/cashFlow/CashFlowAverageSpendingSkeleton';
import CashFlowDetails from '@/modules/cashFlow/cashFlowDetails';
import CashFlowDetailsSkeleton from '@/modules/cashFlow/CashFlowDetailsSkeleton';
import CashFlowNet from '@/modules/cashFlow/cashFlowNet';
import CashFlowNetSkeleton from '@/modules/cashFlow/CashFlowNetSkeleton';
import {
  BankTransaction,
  GetBankTransactionsQueryResult,
  useGetBankTransactionsChartQuery,
  useGetBankTransactionsOverviewQuery,
  useGetBankTransactionsQuery,
} from '@/store/transactionsApi';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { toApiDate } from '@/utils/date';
import { useEffect, useMemo, useState } from 'react';
import FilterSelectionModal from '@/components/modals/FilterSelectionModal';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import {
  selectExcludedAccounts,
  selectExcludedCategories,
  selectExcludedAccountTypes,
  selectExcludedInstitutions,
} from '@/store/filterSlice';
import { useLocalization } from '@/hooks/useLocalization';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import TagSelectorDropdown from '@/components/common/TagSelectorDropdown';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';
import { buildCashFlowSummaryFromChart } from '@/utils/cashFlowSummary';
import { ContentContainerVariant } from '@/widgets/contentContainer';

const { startDate: initialPageStartDate, endDate: initialPageEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

interface CashFlowWidgetProps extends StandardWidgetProps {
  investorId?: number;
}

type CashFlowQueryError = GetBankTransactionsQueryResult['error'];

function hasErrorMessage(value: object): value is { message: string } {
  return 'message' in value && typeof value.message === 'string';
}

function getQueryErrorMessage(
  error: CashFlowQueryError,
  fallbackMessage: string,
) {
  if (!error) {
    return fallbackMessage;
  }

  if (
    'data' in error &&
    typeof error.data === 'object' &&
    error.data !== null &&
    hasErrorMessage(error.data)
  ) {
    return error.data.message;
  }

  if ('error' in error && typeof error.error === 'string') {
    return error.error;
  }

  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallbackMessage;
}

const CashFlow: React.FC<CashFlowWidgetProps> = ({
  hideTitle,
  investorId,
  container = 'page',
}: CashFlowWidgetProps) => {
  const authLoaded = useAuthIsLoaded();
  const [showFilter, setShowFilter] = useState(false);

  // Get user ID and role for filter persistence
  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  // Get persisted filter values
  const excludedAccounts = useSelector((state: WARootState) =>
    selectExcludedAccounts(state, userId, currentRole),
  );
  const excludedCategories = useSelector((state: WARootState) =>
    selectExcludedCategories(state, userId, currentRole),
  );
  const excludedAccountTypes = useSelector((state: WARootState) =>
    selectExcludedAccountTypes(state, userId, currentRole),
  );
  const excludedInstitutions = useSelector((state: WARootState) =>
    selectExcludedInstitutions(state, userId, currentRole),
  );
  const effectiveExclusions = useEffectiveExclusions({
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
  });

  // Calculate total filter count
  const filterCount =
    excludedAccounts.length +
    excludedCategories.length +
    excludedAccountTypes.length +
    excludedInstitutions.length;

  // Get firm-configured default view from menu settings
  const { defaultView: firmDefaultView } = usePageDefaults(
    '/CashFlow',
    'cashflow',
  );
  const { t } = useLocalization();
  const showCashFlowTagSelector = useShowTagSelectorDropdown();
  const theme = useTheme();
  const {
    data: advisorGroupSettings,
    isLoading: advisorGroupSettingsLoading,
    isError: advisorGroupSettingsIsError,
    error: advisorGroupSettingsError,
  } = useGetAdvisorGroupSettingsQuery(undefined, { skip: !authLoaded });
  const { startDate, endDate, toggleDateRange, handleDateRangeChange } =
    usePageDates({
      initialPageStartDate,
      initialPageEndDate,
    });

  const formattedStartDate = toApiDate(startDate || initialPageStartDate);
  const formattedEndDate = toApiDate(endDate || initialPageEndDate);

  const firmHidesTransfers = useMemo(
    () =>
      !AdvisorGroupSettings(advisorGroupSettings).getByKey(
        'ShowAllTransactionsInCashFlow',
      ),
    [advisorGroupSettings],
  );
  const [chosenHideTransfers, chooseHideTransfers] = useHideTransfersChoice(
    userId,
    currentRole,
    firmHidesTransfers,
  );
  const hideTransfers =
    container === 'flyout' ? firmHidesTransfers : chosenHideTransfers;
  const cashFlowSettingsReady = authLoaded && !!advisorGroupSettings;

  const hideTransfersToggle = cashFlowSettingsReady ? (
    <FormControlLabel
      control={
        <Checkbox
          size='small'
          checked={hideTransfers}
          onChange={(event) => {
            chooseHideTransfers(event.target.checked);
          }}
        />
      }
      label={t('cashFlowPage.hideTransfers')}
      sx={{ mr: 0, whiteSpace: 'nowrap' }}
    />
  ) : null;

  // Prepare filter object for transactions query
  const transactionFilters = useMemo(() => {
    const hasFilters =
      effectiveExclusions.excludedAccounts.length > 0 ||
      effectiveExclusions.excludedCategories.length > 0 ||
      effectiveExclusions.excludedAccountTypes.length > 0;

    if (!hasFilters) {
      return {};
    }

    return {
      ...(effectiveExclusions.excludedAccounts.length > 0
        ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
        : {}),
      ...(effectiveExclusions.excludedCategories.length > 0
        ? { ExcludedCategories: effectiveExclusions.excludedCategories }
        : {}),
      ...(effectiveExclusions.excludedAccountTypes.length > 0
        ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
        : {}),
    };
  }, [effectiveExclusions]);

  const cashFlowSupportQueryArgs = useMemo(
    () => ({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      hideTransfers,
      ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
      ...transactionFilters,
    }),
    [
      formattedEndDate,
      formattedStartDate,
      hideTransfers,
      investorId,
      transactionFilters,
    ],
  );

  const bankTransactionsQuery = useGetBankTransactionsQuery(
    cashFlowSettingsReady
      ? {
          ...cashFlowSupportQueryArgs,
          limit: 500,
          page: 1,
        }
      : skipToken,
  );

  const bankTransactionsChartQuery = useGetBankTransactionsChartQuery(
    cashFlowSettingsReady ? cashFlowSupportQueryArgs : skipToken,
  );

  const bankTransactionsOverviewQuery = useGetBankTransactionsOverviewQuery(
    cashFlowSettingsReady ? cashFlowSupportQueryArgs : skipToken,
  );

  const {
    currentData: bankTransactionsCurrentData,
    isLoading: bankTransactionsLoading,
    isError: bankTransactionsIsError,
    error: bankTransactionsError,
  } = bankTransactionsQuery;

  const {
    data: bankTransactionsChartData,
    currentData: bankTransactionsChartCurrentData,
    isLoading: bankTransactionsChartLoading,
    isError: bankTransactionsChartIsError,
    error: bankTransactionsChartError,
  } = bankTransactionsChartQuery;

  const {
    currentData: bankTransactionsOverviewCurrentData,
    isLoading: bankTransactionsOverviewLoading,
    isError: bankTransactionsOverviewIsError,
    error: bankTransactionsOverviewError,
  } = bankTransactionsOverviewQuery;

  const cashFlowSummary = useMemo(
    () =>
      bankTransactionsChartData
        ? buildCashFlowSummaryFromChart(
            bankTransactionsChartData,
            formattedStartDate,
            formattedEndDate,
          )
        : null,
    [bankTransactionsChartData, formattedEndDate, formattedStartDate],
  );

  const getCashFlowErrorMessage = () => {
    const error =
      advisorGroupSettingsError ||
      bankTransactionsChartError ||
      bankTransactionsOverviewError ||
      bankTransactionsError;

    return getQueryErrorMessage(error, t('Error loading cash flow data.'));
  };

  // Gate the skeleton on first-load (`isLoading`) only, never on a background
  // refetch (`isFetching`). A bulk upload invalidates the bank transaction tags,
  // which refetches these queries; if the skeleton swapped in on every refetch
  // it would unmount <CashFlowDetails/> — and with it the open Add Transaction
  // modal — before the user could read the success/validation message (EUD-414).
  const isCashFlowLoading =
    (advisorGroupSettingsLoading ||
      bankTransactionsLoading ||
      bankTransactionsChartLoading ||
      bankTransactionsOverviewLoading) &&
    !advisorGroupSettingsIsError &&
    !bankTransactionsIsError &&
    !bankTransactionsChartIsError &&
    !bankTransactionsOverviewIsError;
  // Use `currentData` (not `data`) so we distinguish the two refetch kinds:
  // `data` persists across arg changes, but `currentData` clears while a NEW arg
  // is in flight. So a date/filter change drops to the skeleton instead of
  // rendering stale totals against the new dates, while a same-arg invalidation
  // refetch (the upload above) keeps `currentData` and stays mounted — letting
  // the data update in place and the modal survive to show its message.
  const hasRenderableSummary =
    !!cashFlowSummary &&
    !!bankTransactionsChartCurrentData &&
    !!bankTransactionsCurrentData &&
    !!bankTransactionsOverviewCurrentData;

  const cashFlowTransactions = useMemo((): BankTransaction[] => {
    return bankTransactionsCurrentData?.transactions ?? [];
  }, [bankTransactionsCurrentData]);

  // A background refetch (e.g. the bulk-upload tag invalidation) keeps the
  // content mounted instead of swapping in the skeleton, so without a cue the
  // page looks frozen while data updates. Show a thin progress bar while a
  // same-arg refetch is in flight (content already on screen, not first load).
  const isCashFlowRefreshing =
    !isCashFlowLoading &&
    hasRenderableSummary &&
    (bankTransactionsQuery.isFetching ||
      bankTransactionsChartQuery.isFetching ||
      bankTransactionsOverviewQuery.isFetching);

  // Safety net so the bar can never appear permanently stuck: it is already tied
  // to RTK Query's `isFetching` (which always clears when a request settles,
  // success or error), but if a request truly hangs we force the bar to hide
  // after a generous timeout. Resets whenever a refresh starts/finishes.
  const [refreshHidden, setRefreshHidden] = useState(false);
  useEffect(() => {
    if (!isCashFlowRefreshing) {
      setRefreshHidden(false);
      return;
    }
    const timeoutId = setTimeout(() => {
      setRefreshHidden(true);
    }, 30000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isCashFlowRefreshing]);

  const showRefreshBar = isCashFlowRefreshing && !refreshHidden;

  const Widget = () => {
    return (
      <WidgetHeader
        title={
          hideTitle ? (
            ''
          ) : showCashFlowTagSelector ? (
            <TagSelectorDropdown
              size='small'
              showSelector={false}
              inlineTitle={t('Cash Flow')}
              inlineTitleVariant='h5'
            />
          ) : (
            t('Cash Flow')
          )
        }
        rightContent={
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            flexWrap='wrap'
            gap={{ xs: 1, sm: 2 }}
          >
            {hideTransfersToggle}
            <PageHeaderMenu
              startDate={startDate}
              endDate={endDate}
              showDatePicker
              toggleDateRange={toggleDateRange}
              handleDateRangeChange={handleDateRangeChange}
              showFilter={true}
              onFilterClick={() => {
                setShowFilter(true);
              }}
              filterButtonText={t('filter')}
              filterCount={filterCount > 0 ? filterCount : undefined}
            />
          </Stack>
        }
      />
    );
  };

  const Flyout = () => {
    return (
      <WidgetHeader
        title={
          hideTitle ? (
            ''
          ) : showCashFlowTagSelector ? (
            <TagSelectorDropdown
              size='small'
              showSelector={false}
              inlineTitle={t('Cash Flow')}
              inlineTitleVariant='h5'
            />
          ) : (
            t('Cash Flow')
          )
        }
        rightContent={
          <PageHeaderMenu
            startDate={startDate}
            endDate={endDate}
            showDatePicker
            toggleDateRange={toggleDateRange}
            handleDateRangeChange={handleDateRangeChange}
            showFilter={false}
          />
        }
      />
    );
  };

  const Page = () => {
    return (
      <BodyHead
        HeadName={
          hideTitle ? (
            ''
          ) : showCashFlowTagSelector ? (
            <TagSelectorDropdown
              size='small'
              showSelector={false}
              inlineTitle={t('Cash Flow')}
              inlineTitleVariant='h5'
            />
          ) : (
            t('Cash Flow')
          )
        }
        HeadMenu={
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            flexWrap='wrap'
            gap={{ xs: 1, sm: 2 }}
          >
            {hideTransfersToggle}
            <PageHeaderMenu
              startDate={startDate}
              endDate={endDate}
              showDatePicker
              toggleDateRange={toggleDateRange}
              handleDateRangeChange={handleDateRangeChange}
              showFilter={true}
              onFilterClick={() => {
                setShowFilter(true);
              }}
              filterButtonText={t('filter')}
              filterCount={filterCount > 0 ? filterCount : undefined}
            />
          </Stack>
        }
      />
    );
  };

  const content = (container: ContentContainerVariant) => {
    switch (container) {
      case 'flyout':
        return Flyout();
      case 'widget':
        return Widget();
      default:
        return Page();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {showRefreshBar && (
        <LinearProgress
          aria-label={t('cashFlowPage.refreshingData')}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (muiTheme) => muiTheme.zIndex.appBar,
          }}
        />
      )}
      {content(container)}

      {/* Wizard Eligibility Component */}
      <WizardEligibility
        startDate={formattedStartDate}
        endDate={formattedEndDate}
        investorId={investorId}
      />

      <Box
        sx={{
          padding: '24px',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.down(800)]: { padding: '0px' },
        }}
        gap={3}
      >
        {(advisorGroupSettingsIsError ||
          bankTransactionsIsError ||
          bankTransactionsChartIsError ||
          bankTransactionsOverviewIsError) && (
          <Typography
            color='error'
            sx={{ p: 2 }}
          >
            {getCashFlowErrorMessage()}
          </Typography>
        )}

        {!advisorGroupSettingsIsError &&
          !bankTransactionsIsError &&
          !bankTransactionsChartIsError &&
          !bankTransactionsOverviewIsError && (
            <Box>
              <Stack
                gap={3}
                direction={{ md: 'row', sm: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                  }}
                >
                  {isCashFlowLoading || !hasRenderableSummary ? (
                    <CashFlowNetSkeleton />
                  ) : (
                    <CashFlowNet data={cashFlowSummary.netCashFlowData} />
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                  }}
                >
                  {isCashFlowLoading || !hasRenderableSummary ? (
                    <CashFlowAverageSpendingSkeleton />
                  ) : (
                    <CashFlowAverageSpending
                      data={cashFlowSummary.averageSpendingData}
                    />
                  )}
                </Box>
              </Stack>

              <Grid
                container
                spacing={3}
                sx={{ height: '100%', flexGrow: 1, mt: 3 }}
              >
                <Grid
                  size={{ xs: 12 }}
                  sx={{ flexGrow: 1 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {isCashFlowLoading || !hasRenderableSummary ? (
                      <CashFlowDetailsSkeleton />
                    ) : (
                      <CashFlowDetails
                        incomeByCategory={cashFlowSummary.incomeByCategory}
                        spendingByCategory={cashFlowSummary.spendingByCategory}
                        topAmount={cashFlowSummary.topAmount}
                        transactions={cashFlowTransactions}
                        startDate={formattedStartDate}
                        endDate={formattedEndDate}
                        investorId={investorId}
                        firmDefaultView={
                          firmDefaultView as ViewMode | undefined
                        }
                        container={container}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
      </Box>
      {showFilter && (
        <FilterSelectionModal
          open={showFilter}
          onClose={() => {
            setShowFilter(false);
          }}
          onApply={() => {
            // Filters are already saved to Redux/localStorage in FilterSelectionModal's handleApply
          }}
          initialExcludedAccounts={excludedAccounts}
          initialExcludedCategories={excludedCategories}
          initialExcludedAccountTypes={excludedAccountTypes}
          initialExcludedInstitutions={excludedInstitutions}
        />
      )}
    </Box>
  );
};

export default CashFlow;
